/**
 * 살점 · 피 — 이 게임에서 가장 어려운 재질.
 *
 * 붉은 MeshStandardMaterial 한 장으로는 절대 고기가 되지 않는다. 플라스틱 덩어리로 보인다.
 * 살이 살로 읽히려면 세 가지가 동시에 있어야 한다:
 *
 *  1. **표면하산란(SSS)** — 빛이 살을 뚫고 들어갔다 뒤로 새어 나온다. 손전등을 손바닥에
 *     대면 붉게 비치는 그것. 이게 없으면 어떤 텍스처를 붙여도 돌멩이다.
 *  2. **젖은 막** — 생고기 표면엔 얇은 육즙 막이 있다. 확산 반사가 아니라 그 위에
 *     또렷한 정반사가 한 겹 더 얹힌다 (clearcoat).
 *  3. **결** — 근섬유가 한 방향으로 흐르고, 지방이 그 사이를 갈라진 나뭇가지처럼 파고든다.
 *     이 두 방향성이 없으면 그냥 붉은 노이즈다.
 *
 * three 에 SSS 재질이 없어서 물리 재질의 조명 루프에 직접 항을 하나 끼워 넣는다.
 */

import * as THREE from 'three';
import { mergeVertices } from 'three/addons/utils/BufferGeometryUtils.js';

function mulberry32(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ---------------------------------------------------------------- 텍스처

const SIZE = 512;

/** 캔버스 경계를 넘는 획은 3×3 으로 겹쳐 그려야 이음매가 안 생긴다 */
function tile9(ctx: CanvasRenderingContext2D, size: number, draw: () => void) {
  for (let ox = -1; ox <= 1; ox++) {
    for (let oy = -1; oy <= 1; oy++) {
      ctx.save();
      ctx.translate(ox * size, oy * size);
      draw();
      ctx.restore();
    }
  }
}

/**
 * 생살 알베도.
 *
 * 그리는 순서가 곧 살의 단면 순서다: 핏빛 바탕 → 근섬유 다발 → 지방 마블링 →
 * 힘줄 막 → 고인 피. 순서를 바꾸면 지방이 피 위로 떠서 스티로폼처럼 보인다.
 */
function fleshAlbedo(seed: number) {
  const cv = document.createElement('canvas');
  cv.width = cv.height = SIZE;
  const ctx = cv.getContext('2d')!;
  const rnd = mulberry32(seed);
  const S = SIZE;

  ctx.fillStyle = '#54150f';
  ctx.fillRect(0, 0, S, S);

  // 부위마다 색이 다르다 — 붉은 살과 흰 살이 얼룩덜룩 섞인다
  tile9(ctx, S, () => {
    for (let i = 0; i < 26; i++) {
      const x = rnd() * S;
      const y = rnd() * S;
      const r = S * (0.08 + rnd() * 0.2);
      const g = ctx.createRadialGradient(x, y, 0, x, y, r);
      const warm = rnd();
      const c = warm > 0.5 ? [112, 34, 25] : [62, 16, 16];
      g.addColorStop(0, `rgba(${c[0]},${c[1]},${c[2]},0.5)`);
      g.addColorStop(1, `rgba(${c[0]},${c[1]},${c[2]},0)`);
      ctx.fillStyle = g;
      ctx.fillRect(x - r, y - r, r * 2, r * 2);
    }
  });

  // 근섬유 — 한 방향으로 흐르되 완전히 평행하진 않다. 다발째 휜다
  const fiberAngle = -0.22;
  tile9(ctx, S, () => {
    ctx.save();
    ctx.translate(S / 2, S / 2);
    ctx.rotate(fiberAngle);
    ctx.translate(-S / 2, -S / 2);
    for (let i = 0; i < 240; i++) {
      const y = rnd() * S * 1.5 - S * 0.25;
      const bend = (rnd() - 0.5) * 26;
      const dark = rnd() > 0.4;
      ctx.strokeStyle = dark
        ? `rgba(58,16,16,${0.1 + rnd() * 0.22})`
        : `rgba(158,80,66,${0.05 + rnd() * 0.11})`;
      ctx.lineWidth = 0.7 + rnd() * 2.4;
      ctx.beginPath();
      ctx.moveTo(-S * 0.3, y);
      ctx.bezierCurveTo(S * 0.2, y + bend, S * 0.7, y - bend, S * 1.3, y + bend * 0.4);
      ctx.stroke();
    }
    ctx.restore();
  });

  // 지방 마블링 — 굵은 줄기에서 갈라져 나가는 가지. 무작위 점으로는 절대 안 된다
  const branch = (x: number, y: number, ang: number, len: number, wide: number, depth: number) => {
    if (depth > 4 || len < 3) return;
    const x2 = x + Math.cos(ang) * len;
    const y2 = y + Math.sin(ang) * len;
    ctx.strokeStyle = `rgba(196,178,144,${0.3 - depth * 0.055})`;
    ctx.lineWidth = wide;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.quadraticCurveTo(
      (x + x2) / 2 + (rnd() - 0.5) * len * 0.4,
      (y + y2) / 2 + (rnd() - 0.5) * len * 0.4,
      x2,
      y2,
    );
    ctx.stroke();
    const kids = rnd() > 0.35 ? 2 : 1;
    for (let k = 0; k < kids; k++) {
      branch(
        x2,
        y2,
        ang + (rnd() - 0.5) * 1.1,
        len * (0.58 + rnd() * 0.24),
        wide * 0.66,
        depth + 1,
      );
    }
  };
  tile9(ctx, S, () => {
    for (let i = 0; i < 14; i++) {
      branch(rnd() * S, rnd() * S, fiberAngle + (rnd() - 0.5) * 0.9, 28 + rnd() * 40, 3 + rnd() * 3.5, 0);
    }
  });

  // 힘줄 막 — 넓고 얇게 덮인 은빛 껍질. 아주 옅어야 한다
  tile9(ctx, S, () => {
    for (let i = 0; i < 7; i++) {
      const x = rnd() * S;
      const y = rnd() * S;
      const r = S * (0.05 + rnd() * 0.09);
      const g = ctx.createRadialGradient(x, y, r * 0.2, x, y, r);
      g.addColorStop(0, 'rgba(190,178,158,0.14)');
      g.addColorStop(1, 'rgba(222,214,196,0)');
      ctx.fillStyle = g;
      ctx.fillRect(x - r, y - r, r * 2, r * 2);
    }
  });

  // 고인 피 — 가장 어둡고 가장 젖은 자리
  tile9(ctx, S, () => {
    for (let i = 0; i < 26; i++) {
      const x = rnd() * S;
      const y = rnd() * S;
      const r = S * (0.02 + rnd() * 0.1);
      const g = ctx.createRadialGradient(x, y, 0, x, y, r);
      g.addColorStop(0, 'rgba(36,5,7,0.8)');
      g.addColorStop(1, 'rgba(48,8,10,0)');
      ctx.fillStyle = g;
      ctx.fillRect(x - r, y - r, r * 2, r * 2);
    }
  });

  return ctx.getImageData(0, 0, S, S);
}

/**
 * 알베도 하나에서 법선·거칠기를 파생한다.
 *
 * 살은 색과 요철이 같은 원인에서 나온다 — 어두운 섬유 골, 밝은 지방 융기.
 * 그래서 따로 그리는 게 아니라 **밝기를 높이로 읽는다**. 대신 거칠기는 색조로 나눈다:
 * 지방(채도 낮고 밝음)은 뿌옇고, 고인 피(어둡고 붉음)는 거울처럼 젖어 있다.
 */
function deriveMaps(src: ImageData) {
  const S = SIZE;
  const d = src.data;
  const lum = new Float32Array(S * S);
  const rough = new Uint8ClampedArray(S * S * 4);

  for (let i = 0; i < S * S; i++) {
    const r = d[i * 4] / 255;
    const g = d[i * 4 + 1] / 255;
    const b = d[i * 4 + 2] / 255;
    const l = 0.299 * r + 0.587 * g + 0.114 * b;
    lum[i] = l;
    const sat = Math.max(r, g, b) - Math.min(r, g, b);
    // 지방일수록 뿌옇게, 어두운 핏자국일수록 젖게
    const fat = Math.max(0, (l - 0.45) * 2) * Math.max(0, 1 - sat * 3.2);
    const wet = Math.max(0, 0.34 - l) * 2.4;
    const v = Math.round(255 * Math.min(0.86, Math.max(0.14, 0.44 + fat * 0.42 - wet * 0.3)));
    rough[i * 4] = rough[i * 4 + 1] = rough[i * 4 + 2] = v;
    rough[i * 4 + 3] = 255;
  }

  // 소벨로 법선 — 감싸듯 읽어야 이음매에서 선이 안 생긴다
  const nrm = new Uint8ClampedArray(S * S * 4);
  const at = (x: number, y: number) => lum[((y + S) % S) * S + ((x + S) % S)];
  const STR = 2.6;
  for (let y = 0; y < S; y++) {
    for (let x = 0; x < S; x++) {
      const dx =
        at(x - 1, y - 1) + 2 * at(x - 1, y) + at(x - 1, y + 1) -
        (at(x + 1, y - 1) + 2 * at(x + 1, y) + at(x + 1, y + 1));
      const dy =
        at(x - 1, y - 1) + 2 * at(x, y - 1) + at(x + 1, y - 1) -
        (at(x - 1, y + 1) + 2 * at(x, y + 1) + at(x + 1, y + 1));
      const nx = dx * STR;
      const ny = dy * STR;
      const len = Math.hypot(nx, ny, 1);
      const i = (y * S + x) * 4;
      nrm[i] = ((nx / len) * 0.5 + 0.5) * 255;
      nrm[i + 1] = ((ny / len) * 0.5 + 0.5) * 255;
      nrm[i + 2] = (1 / len) * 0.5 * 255 + 127.5;
      nrm[i + 3] = 255;
    }
  }
  return { rough, nrm };
}

function texFrom(data: Uint8ClampedArray | ImageData, srgb: boolean) {
  const t =
    data instanceof ImageData
      ? new THREE.DataTexture(data.data, SIZE, SIZE)
      : new THREE.DataTexture(data, SIZE, SIZE);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
  t.anisotropy = 8;
  t.generateMipmaps = true;
  t.minFilter = THREE.LinearMipmapLinearFilter;
  t.needsUpdate = true;
  return t;
}

// ---------------------------------------------------------------- 재질

/**
 * 조명 루프에 표면하산란 항을 끼워 넣는다.
 *
 * three 의 물리 재질은 빛마다 `RE_Direct(...)` 를 부른다. 그 직후에 항을 하나 더 더한다:
 *  - **back**  시선이 광원을 마주볼수록 커진다 → 살 뒤에서 빛이 배어 나온다
 *  - **wrap**  법선이 빛에서 등져도 0 으로 뚝 끊기지 않는다 → 그림자 경계가 살처럼 물러난다
 *
 * `directLight.color` 는 이 지점에서 이미 그림자가 곱해져 있다. 그대로 써야
 * 그늘 속 고기가 혼자 빛나는 사고가 안 난다.
 */
function injectSSS(mat: THREE.MeshPhysicalMaterial, color: THREE.Color, amount: number, power: number) {
  mat.onBeforeCompile = (shader) => {
    shader.uniforms.uSSSColor = { value: color };
    shader.uniforms.uSSSAmount = { value: amount };
    shader.uniforms.uSSSPower = { value: power };
    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <lights_physical_pars_fragment>',
        `#include <lights_physical_pars_fragment>
         uniform vec3 uSSSColor;
         uniform float uSSSAmount;
         uniform float uSSSPower;`,
      )
      .replaceAll(
        'RE_Direct( directLight, geometryPosition, geometryNormal, geometryViewDir, geometryClearcoatNormal, material, reflectedLight );',
        `RE_Direct( directLight, geometryPosition, geometryNormal, geometryViewDir, geometryClearcoatNormal, material, reflectedLight );
         {
           float back = pow( saturate( dot( geometryViewDir, -directLight.direction ) ), uSSSPower );
           float wrap = saturate( ( dot( geometryNormal, directLight.direction ) + 0.5 ) / 1.5 );
           reflectedLight.directDiffuse += directLight.color * uSSSColor * ( back * 1.05 + wrap * 0.09 ) * uSSSAmount;
         }`,
      );
  };
  // 셰이더를 고쳤으니 프로그램 캐시를 분리한다. 안 하면 일반 물리 재질과 섞여 버린다
  mat.customProgramCacheKey = () => `sss${amount}_${power}`;
}

let cached: { flesh: THREE.Material; blood: THREE.Material } | null = null;

/** 생살 + 피 재질. 텍스처 생성이 무거워 한 번만 만든다 */
export function fleshMaterials() {
  if (cached) return cached;

  const albedo = fleshAlbedo(4471);
  const { rough, nrm } = deriveMaps(albedo);

  const flesh = new THREE.MeshPhysicalMaterial({
    map: texFrom(albedo, true),
    normalMap: texFrom(nrm, false),
    roughnessMap: texFrom(rough, false),
    normalScale: new THREE.Vector2(1.7, 1.7),
    roughness: 1,
    metalness: 0,
    // 육즙 막 — 확산 반사 위에 얹히는 또렷한 한 겹
    clearcoat: 0.34,
    clearcoatRoughness: 0.46,
    // 스치는 각도에서 붉게 뜨는 솜털 같은 반사
    sheen: 0.3,
    sheenColor: new THREE.Color('#ff7a63'),
    sheenRoughness: 0.75,
    vertexColors: true,
  });
  injectSSS(flesh, new THREE.Color('#a8231400'.slice(0, 7)), 0.2, 3.4);

  // 피는 살보다 훨씬 젖어 있고 훨씬 어둡다. 대신 얕게 고이면 빛이 통과한다
  // 바닥에 고인 핏자국은 두께 없는 한 장이다 — 양면이어야 시선이 낮을 때 사라지지 않고,
  // 게이트(index.ts twoSided)도 이걸 보고 「뚫린 몸체」에서 빼 준다
  const blood = new THREE.MeshPhysicalMaterial({
    color: '#3d0f0d',
    roughness: 0.34,
    metalness: 0,
    clearcoat: 0.45,
    clearcoatRoughness: 0.22,
    side: THREE.DoubleSide,
    vertexColors: true,
  });
  injectSSS(blood, new THREE.Color('#6b1110'), 0.3, 3.6);

  cached = { flesh, blood };
  return cached;
}

// ---------------------------------------------------------------- 지오메트리

/** 3D 값 노이즈 — 같은 자리는 항상 같은 값이라야 조각난 정점이 안 갈라진다 */
function noise3(seed: number) {
  const rnd = mulberry32(seed);
  const perm = new Float32Array(512);
  for (let i = 0; i < 512; i++) perm[i] = rnd();
  const h = (x: number, y: number, z: number) => {
    const i = (Math.imul(x, 73856093) ^ Math.imul(y, 19349663) ^ Math.imul(z, 83492791)) >>> 0;
    return perm[i & 511];
  };
  const fade = (t: number) => t * t * (3 - 2 * t);
  return (x: number, y: number, z: number) => {
    const xi = Math.floor(x);
    const yi = Math.floor(y);
    const zi = Math.floor(z);
    const xf = fade(x - xi);
    const yf = fade(y - yi);
    const zf = fade(z - zi);
    let v = 0;
    for (let dz = 0; dz < 2; dz++)
      for (let dy = 0; dy < 2; dy++)
        for (let dx = 0; dx < 2; dx++) {
          const wx = dx ? xf : 1 - xf;
          const wy = dy ? yf : 1 - yf;
          const wz = dz ? zf : 1 - zf;
          v += h(xi + dx, yi + dy, zi + dz) * wx * wy * wz;
        }
    return v;
  };
}

export interface ChunkOpts {
  /** 칼로 자른 평평한 단면을 위쪽에 낸다 — 이게 있어야 '썰어 놓은 살'이 된다 */
  cut?: boolean;
  /** 표면 요철 세기 (0 이면 매끈한 덩어리) */
  lumpy?: number;
  /** 삼각형 예산: 1 이면 80면, 2 면 320면 */
  detail?: number;
}

/**
 * 찢긴 살덩이.
 *
 * 정육면체를 깎는 게 아니라 구를 뭉갠다. 고기는 각이 없다 —
 * 상자에 베벨을 준 것과 뭉갠 구는 실루엣부터 다르게 읽힌다.
 */
export function fleshChunk(
  seed: number,
  w: number,
  h: number,
  d: number,
  opts: ChunkOpts = {},
): THREE.BufferGeometry {
  const { cut = false, lumpy = 1, detail = 2 } = opts;
  let g: THREE.BufferGeometry = new THREE.IcosahedronGeometry(0.5, detail);
  // 정점을 합쳐 두지 않으면 같은 자리가 조각조각 나뉘어 법선이 각지게 나온다
  g = mergeVertices(g, 1e-5);

  const n = noise3(seed);
  const p = g.attributes.position as THREE.BufferAttribute;
  const v = new THREE.Vector3();
  for (let i = 0; i < p.count; i++) {
    v.fromBufferAttribute(p, i);
    const dir = v.clone().normalize();
    // 옥타브 셋 — 큰 뭉침 / 근육 다발 / 찢긴 잔결. 둘로는 실루엣이 매끈한 타원에 머문다
    const a = n(dir.x * 2.2 + 11, dir.y * 2.2 + 3, dir.z * 2.2 + 7) - 0.5;
    const b = n(dir.x * 5.3 + 31, dir.y * 5.3 + 17, dir.z * 5.3 + 5) - 0.5;
    const c = n(dir.x * 11.7 + 53, dir.y * 11.7 + 41, dir.z * 11.7 + 23) - 0.5;
    const push = 1 + (a * 0.56 + b * 0.3 + c * 0.13) * lumpy;
    v.copy(dir).multiplyScalar(0.5 * push);
    p.setXYZ(i, v.x, v.y, v.z);
  }

  if (cut) {
    // 위쪽을 평면으로 눌러 자른 면을 만든다. 정점을 지우는 게 아니라 눌러야 껍질이 닫힌 채 남는다
    const cutY = 0.17;
    for (let i = 0; i < p.count; i++) {
      const y = p.getY(i);
      if (y > cutY) p.setY(i, cutY);
    }
  }

  p.needsUpdate = true;
  g.computeVertexNormals();
  g.scale(w, h, d);
  return g;
}

/**
 * 고인 피 웅덩이.
 *
 * 원판을 깔면 물감 자국이 된다. 피는 표면장력 때문에 가장자리가 불규칙하게 번지고
 * 테두리가 살짝 도톰하다(메니스커스). 그래서 일그러진 윤곽을 눌러 뽑고 베벨을 준다.
 */
export function bloodPool(radius: number, seed: number, depth = 0.005): THREE.BufferGeometry {
  const rnd = mulberry32(seed);
  const p1 = rnd() * 6.28;
  const p2 = rnd() * 6.28;
  const p3 = rnd() * 6.28;
  const pts: THREE.Vector2[] = [];
  const N = 40;
  for (let i = 0; i < N; i++) {
    const a = (i / N) * Math.PI * 2;
    const rr =
      radius *
      (1 + 0.2 * Math.sin(a * 2 + p1) + 0.12 * Math.sin(a * 3 + p2) + 0.06 * Math.sin(a * 7 + p3));
    pts.push(new THREE.Vector2(Math.cos(a) * rr, Math.sin(a) * rr));
  }
  const shape = new THREE.Shape(pts);
  const g = new THREE.ExtrudeGeometry(shape, {
    depth: depth * 0.5,
    bevelEnabled: true,
    bevelThickness: depth * 0.5,
    bevelSize: depth * 1.6,
    bevelSegments: 2,
    curveSegments: 1,
  });
  g.rotateX(-Math.PI / 2);
  g.translate(0, depth * 0.5, 0);
  return g;
}
